import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link } from 'react-router-dom';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DynamicsDiagnostics, QaThroughputDiagnostics } from '../qualityApi';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';
import { ResolvedPeriod } from '../../PeriodPicker';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

const QA_THROUGHPUT_TOTAL_KEY = 'Total';
const QA_COLORS = ['#0ea5e9', '#22c55e', '#f97316', '#a855f7', '#eab308', '#ef4444', '#14b8a6'];

const QUALITY_TERMINAL_SETTINGS = '/settings?tab=project&section=quality-settings';

/** Collapsible technical breakdown — keeps the chart as the focal point */
function QaThroughputDetailsCollapsed({ qt }: { qt: QaThroughputDiagnostics }) {
  const statuses = qt.doneStatuses.length ? qt.doneStatuses : [];

  return (
    <details className="dynamics-card__qa-details">
      <summary className="dynamics-card__qa-details-summary">
        Details: target statuses &amp; transitions in this Period
      </summary>
      <div className="dynamics-card__qa-details-body">
        <p className="dynamics-card__qa-details-microcopy">
          Data comes from synced Jira changelog. We only count transitions that{' '}
          <strong>end in</strong> statuses you marked as Terminal (Done) — same list everywhere on
          the Quality tab.
        </p>
        <p className="dynamics-card__qa-details-inline-label">Counted targets</p>
        {statuses.length > 0 ? (
          <ul className="dynamics-card__qa-throughput-status-list">
            {statuses.map((s) => (
              <li key={s}>
                <span className="dynamics-card__qa-throughput-status-pill">{s}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="dynamics-card__qa-throughput-def-empty">
            Configure Terminal statuses under Quality.
          </p>
        )}
        <p className="dynamics-card__qa-details-inline-label">Common paths (historical snapshot)</p>
        {qt.topTransitionEdges.length > 0 ? (
          <ol className="dynamics-card__qa-throughput-edge-list dynamics-card__qa-throughput-edge-list--compact">
            {qt.topTransitionEdges.map((e, i) => (
              <li key={`${i}-${String(e.from)}-${e.to}`}>
                <span className="dynamics-card__qa-edge-from">
                  {e.from === null ? '—' : e.from}
                </span>
                <span className="dynamics-card__qa-edge-arrow" aria-hidden>
                  →
                </span>
                <span className="dynamics-card__qa-edge-to">{e.to}</span>
                <span className="dynamics-card__qa-edge-count">{e.count}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="dynamics-card__qa-throughput-def-empty dynamics-card__qa-throughput-def-empty--boxed">
            No matching transitions this Period — widen the Period or run sync with changelog
            history.
          </p>
        )}
        <p className="dynamics-card__qa-details-more">
          <Link to={QUALITY_TERMINAL_SETTINGS} className="dynamics-empty-diagnostic__link">
            Edit Terminal (Done) statuses
          </Link>
        </p>
      </div>
    </details>
  );
}

export const DynamicsG_QAThroughput: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="G"
      title="QA throughput"
      hint="How often work moves into your done statuses each period — by person when Jira records who clicked the transition. Open Insights for plain-language tips."
      needs="Synced Jira changelog + Terminal statuses mapped in Quality settings."
      metric="qa-throughput"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      chartHeight={320}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.G.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.G.bullets]}
    >
      {(points, _onPointClick, diagnostics) => (
        <Inner points={points as ChartPoint[]} diagnostics={diagnostics ?? null} />
      )}
    </DynamicsChartFrame>
  );
};

interface ChartPoint {
  period: string;
  [qaName: string]: number | string | null | undefined;
}

const Inner: React.FC<{ points: ChartPoint[]; diagnostics: DynamicsDiagnostics | null }> = ({
  points,
  diagnostics,
}) => {
  const plotH = useDynamicsPlotHeight(320);
  const hasTotal = useMemo(
    () => points.some((p) => typeof p[QA_THROUGHPUT_TOTAL_KEY] === 'number'),
    [points]
  );
  const qaNames = useMemo(() => {
    const set = new Set<string>();
    for (const p of points) {
      for (const k of Object.keys(p)) {
        if (k !== 'period' && k !== QA_THROUGHPUT_TOTAL_KEY) set.add(k);
      }
    }
    return Array.from(set);
  }, [points]);

  const qt = diagnostics?.qaThroughput;

  const targetLabel = qt?.doneStatuses?.length
    ? qt.doneStatuses.slice(0, 5).join(', ') + (qt.doneStatuses.length > 5 ? '…' : '')
    : 'your Terminal statuses';

  return (
    <>
      <ResponsiveContainer width="100%" height={plotH}>
        <LineChart data={points} margin={{ ...dynamicsBucketChartMargins }}>
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
          <XAxis {...dynamicsPeriodAxisProps} />
          <YAxis />
          <Tooltip {...DYNAMICS_RECHARTS_TOOLTIP_COMMON} />
          <Legend />
          {hasTotal && (
            <Line
              type="monotone"
              dataKey={QA_THROUGHPUT_TOTAL_KEY}
              name={QA_THROUGHPUT_TOTAL_KEY}
              stroke="#64748b"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={{ r: 3 }}
            />
          )}
          {qaNames.map((name, i) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              name={name}
              stroke={QA_COLORS[i % QA_COLORS.length]}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {qt ? (
        <div className="dynamics-card__qa-below-chart">
          <p className="dynamics-card__qa-lede-one-liner">
            <strong>{QA_THROUGHPUT_TOTAL_KEY}</strong> = every move <strong>into</strong>{' '}
            <span className="dynamics-card__qa-lede-statuses">{targetLabel}</span>
            {' — '}
            <Link to={QUALITY_TERMINAL_SETTINGS} className="dynamics-empty-diagnostic__link">
              Quality · Terminal (Done)
            </Link>
            . Colour lines ≈ top people (Unknown = no author on the transition).
          </p>
          <QaThroughputDetailsCollapsed qt={qt} />
        </div>
      ) : points.length > 0 ? (
        <p className="dynamics-card__qa-lede-one-liner dynamics-card__qa-lede-fallback">
          Definition data missing — refresh. Configure{' '}
          <Link to={QUALITY_TERMINAL_SETTINGS} className="dynamics-empty-diagnostic__link">
            Terminal statuses
          </Link>
          .
        </p>
      ) : null}
    </>
  );
};
